import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useDrawingArea, useXScale } from '@mui/x-charts/hooks';
import { PhysicalActivityStat } from 'services/statsService';

const GOAL_COLOR = '#73BE5E';

// Geometry from Figma "Step count widget" (mobile) and "Step count large" (tablet/desktop).
const LAYOUTS = {
  mobile: {
    height: 253,
    margin: { top: 30, right: 4, bottom: 40, left: 41 },
    lineWidth: 1,
    categoryGapRatio: 0.4,
    // x-charts places tick labels at tickSize + 3px from the axis.
    xLabelTickSize: 8,
    yLabelTickSize: 0,
    underline: { width: 11, offset: 25, strokeWidth: 1 },
  },
  large: {
    height: 293,
    margin: { top: 38, right: 20, bottom: 48, left: 62 },
    lineWidth: 2,
    categoryGapRatio: 0.44,
    xLabelTickSize: 10,
    yLabelTickSize: 5,
    underline: { width: 28, offset: 35, strokeWidth: 2 },
  },
};

type UnderlineStyle = (typeof LAYOUTS)['large']['underline'];

const fontOf = ({ fontFamily, fontSize, fontWeight }: React.CSSProperties) => ({
  fontFamily,
  fontSize,
  fontWeight,
});

interface StepCountChartProps {
  week: PhysicalActivityStat[];
  goal: number;
  maximum: number;
}

const TodayUnderline = ({
  date,
  width,
  offset,
  strokeWidth,
}: UnderlineStyle & { date: string }) => {
  const { palette } = useTheme();
  const { top, height } = useDrawingArea();
  const xScale = useXScale<'band'>();
  const x = (xScale(date) ?? 0) + xScale.bandwidth() / 2;
  const y = top + height + offset;
  return (
    <line
      data-testid="today-underline"
      x1={x - width / 2}
      x2={x + width / 2}
      y1={y}
      y2={y}
      stroke={palette.grey[800]}
      strokeWidth={strokeWidth}
    />
  );
};

export const StepCountChart = ({ week, goal, maximum }: StepCountChartProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const layout = isMobile ? LAYOUTS.mobile : LAYOUTS.large;
  // If there's no goal yet, skip the goal line rather than drawing it on the x-axis.
  const hasGoal = goal > 0;
  const yTicks = [...new Set([0, maximum / 2, goal, maximum])].sort((a, b) => a - b);
  const goalTickPosition = yTicks.indexOf(goal) + 1;
  const goalTickLabel =
    '& .MuiChartsAxis-directionY ' +
    `.MuiChartsAxis-tickContainer:nth-of-type(${goalTickPosition}) .MuiChartsAxis-tickLabel`;
  const xAxis = {
    scaleType: 'band' as const,
    data: week.map((stat) => stat.date),
    valueFormatter: (date: string) =>
      new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'narrow' }),
    tickLabelInterval: () => true,
    tickLabelStyle: fontOf(isMobile ? theme.typography.subheadline : theme.typography.title3),
    tickSize: layout.xLabelTickSize,
    categoryGapRatio: layout.categoryGapRatio,
  };

  return (
    <BarChart
      height={layout.height}
      margin={layout.margin}
      series={[
        {
          data: week.map((stat) => stat.passive_steps),
          color: theme.palette.primary.main,
        },
      ]}
      xAxis={[xAxis]}
      yAxis={[
        {
          min: 0,
          max: maximum,
          tickInterval: yTicks,
          valueFormatter: (value: number) => String(value),
          tickLabelStyle: fontOf(isMobile ? theme.typography.caption : theme.typography.callout),
          tickSize: layout.yLabelTickSize,
        },
      ]}
      grid={{ horizontal: true }}
      slotProps={{ legend: { hidden: true } }}
      skipAnimation
      sx={{
        // Scoped under .MuiChartsAxis-root to outrank x-charts' own axis styles.
        '& .MuiChartsAxis-root .MuiChartsAxis-line': {
          stroke: theme.palette.grey[500],
          strokeWidth: layout.lineWidth,
        },
        // Ticks only position the labels; the marks themselves aren't in the design.
        '& .MuiChartsAxis-root .MuiChartsAxis-tick': { display: 'none' },
        '& .MuiChartsAxis-root .MuiChartsAxis-tickLabel': { fill: theme.palette.grey[800] },
        ...(hasGoal && { [goalTickLabel]: { fill: GOAL_COLOR } }),
        '& .MuiChartsGrid-line': { stroke: theme.palette.brandBackgroundSecondary.main },
      }}
    >
      {hasGoal && (
        <ChartsReferenceLine y={goal} lineStyle={{ stroke: GOAL_COLOR, strokeWidth: 2 }} />
      )}
      {week.length > 0 && (
        <TodayUnderline date={week[week.length - 1].date} {...layout.underline} />
      )}
    </BarChart>
  );
};
